import React, { useState, useEffect } from "react";
import { Home, CreditCard, Target, BarChart3, Settings, Wallet, Tag, Plus, Minus, Menu, ArrowLeftRight, User, TrendingDown, Moon, Sun, TrendingUp, Bot } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { TransactionForm } from "@/components/transactions/TransactionForm";
import TransferModal from "@/components/forms/TransferModal";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Sidebar, SidebarContent, SidebarGroup, SidebarGroupContent, SidebarGroupLabel, SidebarMenu, SidebarMenuButton, SidebarMenuItem, SidebarHeader, SidebarFooter, SidebarTrigger } from "@/components/ui/sidebar";
import { useSidebar } from "@/components/ui/sidebar-utils";
import { ThemeToggle } from "@/components/common/ThemeToggle";
import { UserSection } from "@/components/layout/UserSection";
import { useTheme } from "next-themes";
import { useDebounce } from "@/hooks/useDebounce";
import { cn } from "@/lib/utils";
import { useProfileModal } from "@/contexts/ProfileModalContext";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import lumoLogo from "@/assets/lumo-logo.svg";
import lumoLogoDark from "@/assets/lumo-logo-dark.svg";
import lumoIcon from "@/assets/lumo-icon.svg";
import lumoIconDark from "@/assets/lumo-logo-dark.svg"; // Use dark logo as icon for now
import { Conta } from "@/types/database";

interface AppSidebarProps {
  currentTab: string;
  onTabChange: (tab: string) => void;
  onTransactionSuccess: () => void;
  onOpenAssistant: () => void;
}

const menuItems = [{
  id: "dashboard",
  label: "Dashboard",
  icon: Home
}, {
  id: "accounts",
  label: "Contas",
  icon: Wallet
}, {
  id: "credit-cards",
  label: "Cartões",
  icon: CreditCard
}, {
  id: "transactions",
  label: "Transações",
  icon: ArrowLeftRight
}, {
  id: "goals",
  label: "Metas",
  icon: Target
}, {
  id: "reports",
  label: "Relatórios",
  icon: BarChart3
}, {
  id: "settings",
  label: "Configurações",
  icon: Settings
}];

const settingsSubItems = [{
  id: "categories",
  label: "Categorias",
  icon: Tag
}];

export function AppSidebar({
  currentTab,
  onTabChange,
  onTransactionSuccess,
  onOpenAssistant
}: AppSidebarProps) {
  const { state, toggleSidebar } = useSidebar();
  const { user } = useAuth();
  const { resolvedTheme } = useTheme();
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [isReceitaOpen, setIsReceitaOpen] = useState(false);
  const [isDespesaOpen, setIsDespesaOpen] = useState(false);
  const [isTransferOpen, setIsTransferOpen] = useState(false);
  const [isActionsOpen, setIsActionsOpen] = useState(false);
  const [contas, setContas] = useState<Conta[]>([]);
  
  // Buscar contas do usuário
  useEffect(() => {
    const fetchContas = async () => {
      if (!user?.id) return;
      
      // Se o modal de transferência não estiver aberto, não precisa buscar
      if (!isTransferOpen) return;
      
      const { data, error } = await supabase
        .from('contas')
        .select('*')
        .eq('user_id', user.id)
        .order('nome');
      
      if (error) {
        console.error('Erro ao buscar contas:', error);
        return;
      }
      
      setContas((data || []) as Conta[]);
    };
    
    fetchContas();
  }, [user?.id, isTransferOpen]);

  const handleTransferSuccess = () => {
    onTransactionSuccess();
  };
  
  // Implementar debounce para handlers de clique para melhorar performance
  const debouncedTabChange = useDebounce((tab: string) => {
    onTabChange(tab);
  }, 150);
  
  const debouncedToggleSidebar = useDebounce(() => {
    toggleSidebar();
  }, 100);
  
  const debouncedSettingsToggle = useDebounce(() => {
    setSettingsOpen(!settingsOpen);
  }, 150);
  
  const isCollapsed = state === "collapsed";

  return (
    <Sidebar 
      collapsible="icon" 
      className="border-r border-border bg-card"
    >
      <SidebarHeader>
        <div className={cn(
          "flex items-center transition-all duration-300 ease-in-out",
          isCollapsed ? "flex-col gap-2 px-2 py-3" : "justify-between px-2 py-3"
        )}>
          <div className={cn(
            "flex items-center transition-all duration-300",
            isCollapsed ? "flex-col gap-2" : "gap-2"
          )}>
            {isCollapsed ? (
              <button 
                onClick={debouncedToggleSidebar}
                className="w-12 h-12 p-2 rounded-lg hover:bg-accent/50 transition-colors focus:outline-none focus:ring-2 focus:ring-primary/20 flex items-center justify-center"
              >
                <img 
                  src={resolvedTheme === 'dark' ? lumoIconDark : lumoIcon}
                  alt="Abrir sidebar" 
                  className="h-8 w-8 transition-all duration-300"
                />
              </button>
            ) : (
              <div className="flex items-center gap-2">
                <img 
                  src={resolvedTheme === 'dark' ? lumoLogoDark : lumoLogo}
                  alt="Lumo" 
                  className="h-8 w-auto transition-all duration-300"
                />
                <span className="font-logo font-bold text-xl text-foreground transition-opacity duration-300">Lumo</span>
              </div>
            )}
          </div>
          <div className={cn(
            "flex items-center transition-all duration-300",
            isCollapsed ? "hidden" : "gap-1"
          )}>
            <ThemeToggle />
            <SidebarTrigger className={cn(
              "h-8 w-8 rounded-md transition-all duration-300 hover:bg-accent/50 hover:scale-105",
              "active:scale-95 focus:outline-none focus:ring-2 focus:ring-primary/20"
            )} />
          </div>
        </div>
      </SidebarHeader>

      <SidebarContent className={cn(
        "transition-all duration-300 ease-in-out",
        isCollapsed ? "p-1" : "p-3 sm:p-4"
      )}>
        {/* Transaction Action Buttons */}
        <SidebarGroup className={cn(
          "transition-all duration-300",
          isCollapsed ? "px-1" : ""
        )}>
          <SidebarGroupContent>
            <div className={cn(
              "flex transition-all duration-300 ease-in-out",
              isCollapsed ? "flex-col gap-1 items-center" : "flex-col space-y-2"
            )}>
              <Popover open={isActionsOpen} onOpenChange={setIsActionsOpen}>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    size={isCollapsed ? "icon" : "default"}
                    className={cn(
                      "transition-all duration-200 hover:shadow-medium",
                      isCollapsed
                        ? "h-10 w-10 flex-shrink-0 justify-center"
                        : "w-full h-10 px-4 py-2 justify-start gap-2",
                      "bg-primary text-primary-foreground border-primary hover:bg-primary/90"
                    )}
                    title={isCollapsed ? "Novo lançamento" : undefined}
                  >
                    <Plus className="h-4 w-4" />
                    {!isCollapsed && <span>Novo lançamento</span>}
                  </Button>
                </PopoverTrigger>
                <PopoverContent
                  className="w-56 p-2"
                  side="right"
                  align="start"
                  sideOffset={isCollapsed ? 12 : 8}
                >
                  <div className="space-y-1">
                    <Button
                      variant="ghost"
                      className="w-full justify-start gap-2"
                      onClick={() => {
                        setIsActionsOpen(false);
                        setIsReceitaOpen(true);
                      }}
                    >
                      <TrendingUp className="h-4 w-4 text-green-600" />
                      Receita
                    </Button>
                    <Button
                      variant="ghost"
                      className="w-full justify-start gap-2"
                      onClick={() => {
                        setIsActionsOpen(false);
                        setIsDespesaOpen(true);
                      }}
                    >
                      <TrendingDown className="h-4 w-4 text-red-600" />
                      Despesa
                    </Button>
                    <Button
                      variant="ghost"
                      className="w-full justify-start gap-2"
                      onClick={() => {
                        setIsActionsOpen(false);
                        setIsTransferOpen(true);
                      }}
                    >
                      <ArrowLeftRight className="h-4 w-4 text-purple-600" />
                      Transferência
                    </Button>
                    <Button
                      variant="ghost"
                      className="w-full justify-start gap-2"
                      onClick={() => {
                        setIsActionsOpen(false);
                        onOpenAssistant();
                      }}
                    >
                      <Bot className="h-4 w-4 text-primary" />
                      Assistente Financeiro
                    </Button>
                  </div>
                </PopoverContent>
              </Popover>
            </div>
          </SidebarGroupContent>
        </SidebarGroup>

        <TransferModal
           open={isTransferOpen}
           onOpenChange={setIsTransferOpen}
           onAfterTransfer={handleTransferSuccess}
           contas={contas}
         />

        <Dialog open={isReceitaOpen} onOpenChange={setIsReceitaOpen}>
          <DialogContent className="sm:max-w-[425px] max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="text-green-600 flex items-center gap-2">
                <TrendingUp className="h-5 w-5" />
                Nova Receita
              </DialogTitle>
            </DialogHeader>
            <div className="mt-6">
              <TransactionForm
                tipo="entrada"
                asDialog={false}
                isOpen={isReceitaOpen}
                onSuccess={() => {
                  setIsReceitaOpen(false);
                  onTransactionSuccess();
                }}
              />
            </div>
          </DialogContent>
        </Dialog>

        <Dialog open={isDespesaOpen} onOpenChange={setIsDespesaOpen}>
          <DialogContent className="sm:max-w-[425px] max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="text-red-600 flex items-center gap-2">
                <TrendingDown className="h-5 w-5" />
                Nova Despesa
              </DialogTitle>
            </DialogHeader>
            <div className="mt-6">
              <TransactionForm
                tipo="saida"
                asDialog={false}
                isOpen={isDespesaOpen}
                onSuccess={() => {
                  setIsDespesaOpen(false);
                  onTransactionSuccess();
                }}
              />
            </div>
          </DialogContent>
        </Dialog>

        <SidebarGroup className={cn(
          "transition-all duration-300",
          isCollapsed ? "mt-2" : "mt-4 sm:mt-6"
        )}>
            <SidebarGroupContent>
              <SidebarMenu className={cn(
                "transition-all duration-300",
                isCollapsed ? "space-y-1 mt-1" : "space-y-1 mt-2"
              )}>
              {menuItems.map(item => {
                const Icon = item.icon;
                const isActive = currentTab === item.id;

                // Special handling for Settings with submenu
                if (item.id === "settings") {
                  return (
                    <SidebarMenuItem key={item.id}>
                      <Popover open={settingsOpen} onOpenChange={setSettingsOpen}>
                        <PopoverTrigger asChild>
                          <SidebarMenuButton 
                            isActive={isActive} 
                           className={cn(
                             "w-full flex items-center text-sm transition-colors",
                             isActive 
                               ? "bg-primary text-primary-foreground font-medium shadow-lg" 
                               : "hover:bg-accent/60 hover:text-accent-foreground",
                             isCollapsed 
                               ? "h-10 w-10 justify-center p-0 mx-auto rounded-lg" 
                               : "h-10 justify-start px-3 rounded-md"
                           )}
                            tooltip={isCollapsed ? item.label : undefined}
                          >
                            <Icon className="h-4 w-4 flex-shrink-0" />
                            {!isCollapsed && (
                              <span className="ml-3 text-sm will-change-auto">
                                {item.label}
                              </span>
                            )}
                          </SidebarMenuButton>
                        </PopoverTrigger>
                        <PopoverContent 
                          className={cn(
                            "p-2 transition-all duration-200",
                            isCollapsed ? "w-48 ml-4" : "w-56 ml-2"
                          )}
                          side="right" 
                          align="start"
                          sideOffset={isCollapsed ? 12 : 8}
                        >
                          <div className="space-y-1">
                            {settingsSubItems.map(subItem => {
                              const SubIcon = subItem.icon;
                              const isSubActive = currentTab === subItem.id;
                              return (
                                <button
                                  key={subItem.id}
                                  onClick={() => {
                                    debouncedTabChange(subItem.id);
                                    setSettingsOpen(false);
                                  }}
                                  className={cn(
                                    "w-full flex items-center px-3 py-2 text-sm rounded-md transition-colors",
                                    isSubActive 
                                      ? "bg-primary text-primary-foreground" 
                                      : "hover:bg-accent hover:text-accent-foreground"
                                  )}
                                >
                                  <SubIcon className="h-4 w-4 mr-3 flex-shrink-0" />
                                  <span className="truncate">{subItem.label}</span>
                                </button>
                              );
                            })}
                          </div>
                        </PopoverContent>
                      </Popover>
                    </SidebarMenuItem>
                  );
                }

                return (
                  <SidebarMenuItem key={item.id}>
                    <SidebarMenuButton 
                      onClick={() => debouncedTabChange(item.id)} 
                      isActive={isActive} 
                       className={cn(
                         "w-full flex items-center text-sm transition-colors",
                         isActive 
                           ? "bg-primary text-primary-foreground font-medium shadow-lg" 
                           : "hover:bg-accent/60 hover:text-accent-foreground",
                         isCollapsed 
                           ? "h-10 w-10 justify-center p-0 mx-auto rounded-lg" 
                           : "h-10 justify-start px-3 rounded-md"
                       )}
                      tooltip={isCollapsed ? item.label : undefined}
                    >
                      <Icon className="h-4 w-4 flex-shrink-0" />
                      {!isCollapsed && (
                        <span className="ml-3 text-sm will-change-auto">
                          {item.label}
                        </span>
                      )}
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                );
              })}
              </SidebarMenu>
            </SidebarGroupContent>
        </SidebarGroup>
      </SidebarContent>

      <SidebarFooter className="border-t border-border/50 sticky bottom-0 bg-card z-10">
        {/* User Section - Movido para o footer e fixo */}
        <div className={cn(
          "py-3 transition-all duration-300 ease-in-out",
          isCollapsed ? "px-1" : "px-3"
        )}>
          <UserSection isCollapsed={isCollapsed} />
        </div>
      </SidebarFooter>
    </Sidebar>
  );
}
